import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Grid,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import apiService from "../../services/api";
import { getUserData } from "../../utils/cookieUtils";
import MobileInput from "../../components/forms/MobileInput";
import { useNotification } from "../../contexts/NotificationContext";
import CustomLoading from "../../components/common/CustomLoading";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(
  /\/?$/,
  "/"
)}`;

// Reusable Alert Component
const MessageAlert = ({
  type,
  text,
}: {
  type: "success" | "error";
  text: string;
}) => (
  <Box
    sx={{
      p: 2,
      mb: 2,
      borderRadius: 1,
      backgroundColor: type === "success" ? "#d4edda" : "#f8d7da",
      color: type === "success" ? "#155724" : "#721c24",
      border: `1px solid ${type === "success" ? "#c3e6cb" : "#f5c6cb"}`,
    }}
  >
    <Typography variant="body2">{text}</Typography>
  </Box>
);

const UserProfile = () => {
  const { showNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [originalUserData, setOriginalUserData] = useState<any>(null);

  const user = getUserData();
  const isSuperAdmin = user?.roleid === 1;
  const isNotSuperAdmin = user?.roleid !== 1;

  const [formData, setFormData] = useState({
    fullName: "",
    mobileNo: "",
    mobileNoCode: "+91",
    email: "",
    employeeId: "",
    username: "",
    rolename: "",
    hubname: "",
    profilephoto: "",
    userid: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [initialFormData, setInitialFormData] = useState(formData);

  // Fetch User Data
  useEffect(() => {
    let mounted = true;
    const user = getUserData();

    const loadUserData = async () => {
      if (!user?.userid) return;
      setIsLoading(true);
      try {
        const response = await apiService.getUserByUserId(user.userid);
        const userData = (response as any).data || response;

        if (mounted) {
          setOriginalUserData(userData);
          const freshData = {
            ...formData,
            fullName: userData.name || "",
            mobileNo: userData.phonenumber || "",
            email: userData.emailid || "",
            employeeId: userData.empcode || "",
            username: userData.username || "",
            rolename: userData.rolename || "",
            hubname: userData.hubname || "",
            profilephoto: userData.profilephoto || "",
            userid: userData.userid || "",
            // Reset password fields
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          };
          setFormData(freshData);
          setInitialFormData(freshData);
        }
      } catch {
        mounted &&
          showNotification("Failed to fetch user data.", "error");
      } finally {
        mounted && setIsLoading(false);
      }
    };

    loadUserData();
    return () => {
      mounted = false;
    };
  }, []);

  const handleInputChange = (field: string, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const inputStyle = {
    "& .MuiInputBase-input": {
      fontWeight: 500,
    },
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteImage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setFormData((prev) => ({ ...prev, profilephoto: "" }));
    showNotification("Profile image removed. Save to apply changes.", "info");
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const response = await apiService.uploadImage(file, "UserProfile_Documents", "profile");
      const savedFiles = (response.data as any)?.savedFiles;
      
      if (Array.isArray(savedFiles) && savedFiles.length > 0 && savedFiles[0].filePath) {
        const imageUrl = savedFiles[0].filePath;
        setFormData((prev) => ({ ...prev, profilephoto: imageUrl }));
        showNotification("Profile image updated. Save to apply changes.", "info");
      } else {
        throw new Error("Image URL not found in API response.");
      }
    } catch (error) {
      showNotification("Failed to upload image. Please try again.", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      showNotification("New password and confirm password do not match.", "error");
      setIsLoading(false);
      return;
    }

    try {
      const payload = {
        ...originalUserData,
        userid: parseInt(formData.userid),
        name: formData.fullName,
        emailid: formData.email,
        username: formData.username,
        phonenumber: formData.mobileNo,
        password: formData.newPassword || undefined,
        profilephoto: formData.profilephoto,
      };

      await apiService.createOrUpdateUser(payload);
      showNotification("Profile updated successfully!", "success");
      
      const newInitialData = { ...formData };
      newInitialData.currentPassword = "";
      newInitialData.newPassword = "";
      newInitialData.confirmPassword = "";
      setInitialFormData(newInitialData);

    } catch (err: any) {
      showNotification(err.message || "Failed to update profile.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !originalUserData) {
    return <CustomLoading message="Fetching User Profile..." />;
  }

  return (
    <Container maxWidth="lg">
      <Paper
       elevation={1}
        sx={{ borderRadius: 1, overflow: "hidden", position: "relative"}}
      >
        <Box sx={{ height: 105, bgcolor: "rgba(22, 67, 135, 1)" }} />

        <Box
          sx={{
            p: { xs: 2, md: 4 },
            pt: 0,
            textAlign: "center",
            position: "relative",
            mt: "-120px", // Pulls content up to match avatar size
          }}
        >
          <Box
            position="relative"
            display="inline-block"
            onClick={isSuperAdmin ? undefined : handleAvatarClick}
            sx={{
              cursor: isSuperAdmin ? "default" : "pointer",
              "&:hover .action-icon": {
                opacity: isSuperAdmin ? 0 : 1,
              },
            }}
          >
            <Avatar
              src={
                formData.profilephoto?.startsWith("http")
                  ? formData.profilephoto
                  : formData.profilephoto
                  ? `${BASE_IMAGE_URL}${formData.profilephoto}`
                  : undefined
              }
              sx={{
                width: 170,
                height: 170,
                mx: "auto",
                border: "4px solid white",
                bgcolor: "black",
                color: "white",
                fontSize: "3rem",
              }}
            >
              {(() => {
                if (formData.fullName) {
                  const parts = formData.fullName.split(" ").filter(Boolean);
                  if (parts.length > 1) {
                    return `${parts[0][0]}${
                      parts[parts.length - 1][0]
                    }`.toUpperCase();
                  }
                  if (parts.length === 1 && parts[0].length > 0) {
                    return parts[0][0].toUpperCase();
                  }
                }
                if (formData.username) {
                  return formData.username[0].toUpperCase();
                }
                return "U";
              })()}
            </Avatar>
            <IconButton
              className="action-icon"
              sx={{
                position: "absolute",
                bottom: 10,
                right: 10,
                backgroundColor: "rgba(0, 0, 0, 0.6)",
                opacity: 0,
                transition: "opacity 0.2s",
                "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
              }}
            >
              {isUploading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : (
                <EditIcon sx={{ color: "white", fontSize: "1.2rem" }} />
              )}
            </IconButton>
            {formData.profilephoto && !isUploading && (
              <IconButton
                className="action-icon"
                onClick={handleDeleteImage}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  opacity: 0,
                  transition: "opacity 0.2s",
                  "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.8)" },
                }}
              >
                <DeleteIcon sx={{ color: "white", fontSize: "1.2rem" }} />
              </IconButton>
            )}
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept="image/*"
            style={{ display: "none" }}
          />

          <Box sx={{ mt: 2 }}>
            <Typography variant="h5" fontWeight={600}>
              {formData.fullName || "User"}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {formData.rolename}
            </Typography>
          </Box>

          <Box component="form" sx={{ mt: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e: any) =>
                    handleInputChange("fullName", e.target.value)
                  }
                  InputProps={{ readOnly: isSuperAdmin }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontWeight: 500,
                      cursor: isSuperAdmin ? "not-allowed" : "text",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e: any) =>
                    handleInputChange("email", e.target.value)
                  }
                  InputProps={{ readOnly: isSuperAdmin }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontWeight: 500,
                      cursor: isSuperAdmin ? "not-allowed" : "text",
                    },
                  }}
                />
              </Grid>
              {isNotSuperAdmin && (
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Hub Name"
                    value={formData.hubname}
                    InputProps={{
                      readOnly: true,
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        cursor: "not-allowed",
                        fontWeight: 500,
                      },
                      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgba(0, 0, 0, 0.87)",
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <MobileInput
                  field="mobileNo"
                  formData={formData}
                  onChange={handleInputChange}
                  readOnly={isSuperAdmin}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Username"
                  value={formData.username}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontWeight: 500,
                      cursor: "not-allowed",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Employee Code"
                  value={formData.employeeId}
                  InputProps={{
                    readOnly: true,
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                      fontWeight: 500,
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          {!isSuperAdmin && (
            <Box component="form" sx={{ mt: 4 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    align="left"
                    fontWeight="bold"
                    sx={{ color: "rgba(22, 67, 135, 1)" }}
                  >
                    Change Password
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Current Password"
                    value={formData.currentPassword}
                    onChange={(e: any) =>
                      handleInputChange("currentPassword", e.target.value)
                    }
                    type={showCurrentPassword ? "text" : "password"}
                    sx={inputStyle}
                    disabled={isSuperAdmin}
                    InputProps={{
                      endAdornment: formData.currentPassword && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                            edge="end"
                          >
                            {showCurrentPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="New Password"
                    value={formData.newPassword}
                    onChange={(e: any) =>
                      handleInputChange("newPassword", e.target.value)
                    }
                    type={showNewPassword ? "text" : "password"}
                    sx={inputStyle}
                    disabled={isSuperAdmin}
                    InputProps={{
                      endAdornment: formData.newPassword && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={(e: any) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    type={showConfirmPassword ? "text" : "password"}
                    sx={inputStyle}
                    disabled={isSuperAdmin}
                    InputProps={{
                      endAdornment: formData.confirmPassword && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge="end"
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {!isSuperAdmin && (
            <Box
              sx={{
                mt: 3,
                justifyContent: "center",
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isLoading}
                sx={{
                  borderRadius: "4px",
                  px: 10,
                  backgroundColor: "rgba(22, 67, 135, 1)",
                  "&:hover": {
                    backgroundColor: "rgba(22, 67, 135, 0.85)",
                  },
                }}
              >
                {isLoading ? "Saving..." : "Save"}
              </Button>
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default UserProfile;
